import React from 'react';
import { CalendarIcon, ClockIcon, WrenchIcon, Trash2Icon } from 'lucide-react';
import { motion } from 'framer-motion';
export interface WorkLogEntry {
  date: string;
  description: string;
  hours?: number;
}
export interface TaskAttachment {
  name: string;
  url: string;
  storagePath: string;
  uploadedAt: string;
  size?: number;
}
export interface Task {
  id: string;
  taskId: string;
  title: string;
  customerId: string;
  customerName?: string;
  description: string;
  status: 'todo' | 'in-progress' | 'done' | 'planned' | 'pending';
  category: 'Ladeboks' | 'Andet';
  chargerType?: 'Zaptec 1' | 'Zaptec 2' | 'Andet';
  taskType: 'Tilbudsopgave' | 'Regningsopgave';
  agreedPlanning?: string;
  agreedInstallation?: string;
  carDeliveryDate?: string;
  hours?: number;
  materialPrice?: number;
  hourlyRate?: number;
  quotePrice?: number;
  totalPrice?: number;
  invoiced: boolean;
  subTasks?: string;
  materials?: string[];
  workLog?: WorkLogEntry[];
  attachments?: TaskAttachment[];
}
interface TaskCardProps {
  task: Task;
  index: number;
  onClick: () => void;
  onDelete: (task: Task) => void;
  readOnly?: boolean;
}
const getStatusColor = (status: string) => {
  switch (status) {
    case 'done':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'in-progress':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'planned':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'pending':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'todo':
      return 'bg-stone-100 text-stone-700 border-stone-200';
    default:
      return 'bg-amber-100 text-amber-700 border-amber-200';
  }
};
const getStatusLabel = (status: string) => {
  switch (status) {
    case 'done':
      return 'Afsluttet';
    case 'in-progress':
      return 'I gang';
    case 'planned':
      return 'Planlagt';
    case 'pending':
      return 'Afventende';
    default:
      return 'I gang';
  }
};
export function TaskCard({
  task,
  index,
  onClick,
  onDelete,
  readOnly = false
}: TaskCardProps) {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(task);
  };
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    delay: index * 0.05
  }} className="bg-white rounded-xl p-5 shadow-sm border border-stone-100 hover:shadow-md hover:border-teal-200 transition-all cursor-pointer relative group" data-id="element-780" data-mp="12daelydfvw2k">
      {!readOnly && <button onClick={handleDelete} className="absolute top-3 right-3 p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100" aria-label="Slet opgave" data-id="element-781" data-mp="nm9xz62jgvvc">
          <Trash2Icon className="w-4 h-4" data-id="element-782" data-mp="3r28l9apmh5o6" />
        </button>}
      <div onClick={readOnly ? undefined : onClick} data-id="element-783" data-mp="3w1l7pucl0aq8">
        <div className="flex items-start justify-between mb-3 pr-8" data-id="element-784" data-mp="3w00gadlkd7wl">
          <div className="flex-1" data-id="element-785" data-mp="3oh9c46hp6xrl">
            <div className="flex items-center gap-2 mb-1" data-id="element-786" data-mp="3og7z3wh4g178">
              <h3 className="text-lg font-semibold text-stone-900" data-id="element-787" data-mp="3jksn7a2ocfdg">
                {task.title}
              </h3>
              {task.taskId && <span className="text-xs font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full border border-stone-200" data-id="element-788" data-mp="3ait66hrntbai">
                  {task.taskId}
                </span>}
            </div>
            {task.customerName && <p className="text-sm text-stone-500" data-id="element-789" data-mp="30rez9oxdp97c">{task.customerName}</p>}
          </div>
          <div className="flex flex-col gap-2 items-end" data-id="element-790" data-mp="2vug556dpt295">
            <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(task.status)}`} data-id="element-791" data-mp="2qvbx4j2j1ake">
              {getStatusLabel(task.status)}
            </span>
            <div className="flex gap-1.5" data-id="element-792" data-mp="2htlz2ue53ddn">
              {task.category && <span className="px-2.5 py-0.5 text-xs font-medium rounded-full border bg-blue-50 text-blue-700 border-blue-200" data-id="element-793" data-mp="lr922m8w4osb">
                  {task.category === 'Andet' ? 'Alm. Installation' : task.category}
                </span>}
              {task.taskType && <span className="px-2.5 py-0.5 text-xs font-medium rounded-full border bg-teal-50 text-teal-700 border-teal-200" data-id="element-794" data-mp="gzhcof1h0p3r">
                  {task.taskType}
                </span>}
            </div>
          </div>
        </div>
        {task.description && <p className="text-sm text-stone-600 mb-4 line-clamp-2" data-id="element-795" data-mp="3p893x5c7837m">
            {task.description}
          </p>}
        <div className="space-y-2" data-id="element-796" data-mp="3u7u41j33uhlx">
          {task.agreedPlanning && <div className="flex items-center gap-2 text-sm" data-id="element-797" data-mp="1ngmc51khg4a3">
              <WrenchIcon className="w-4 h-4 text-teal-600 flex-shrink-0" data-id="element-798" data-mp="1ne9t0l2znnvg" />
              <span className="text-stone-500" data-id="element-799" data-mp="1se4hyj37e3dv">Aftalt projektering:</span>
              <span className="text-stone-900 font-medium" data-id="element-800" data-mp="22il0xlbzc0lw">
                {new Date(task.agreedPlanning).toLocaleDateString('da-DK')}
              </span>
            </div>}
          {task.agreedInstallation && <div className="flex items-center gap-2 text-sm" data-id="element-801" data-mp="2bd94n71wpa8l">
              <CalendarIcon className="w-4 h-4 text-teal-600 flex-shrink-0" data-id="element-802" data-mp="26kfyceqshtue" />
              <span className="text-stone-500" data-id="element-803" data-mp="2l9vllkr43h5c">Aftalt installation:</span>
              <span className="text-stone-900 font-medium" data-id="element-804" data-mp="2lbynp35jh0qf">
                {new Date(task.agreedInstallation).toLocaleDateString('da-DK')}
              </span>
            </div>}
          {task.hours !== undefined && <div className="flex items-center gap-2 text-sm" data-id="element-805" data-mp="2q989m8tpyu9i">
              <ClockIcon className="w-4 h-4 text-teal-600 flex-shrink-0" data-id="element-806" data-mp="2q9qbpm32c2pc" />
              <span className="text-stone-500" data-id="element-807" data-mp="zxaokj8b9nxv">Timer:</span>
              <span className="text-stone-900 font-medium" data-id="element-808" data-mp="vv7d3uln6jcl">{task.hours}t</span>
            </div>}
        </div>
        {(task.quotePrice !== undefined || task.totalPrice !== undefined) && <div className="mt-4 pt-4 border-t border-stone-100" data-id="element-809" data-mp="2zyiljwuo6twg">
            <div className="flex items-center justify-between" data-id="element-810" data-mp="300v3wb4cb6a1">
              <span className="text-sm text-stone-500" data-id="element-811" data-mp="34wu0un3qhwm2">
                {task.taskType === 'Tilbudsopgave' ? 'Tilbudspris' : 'Samlet pris'}
              </span>
              <span className="text-lg font-semibold text-stone-900" data-id="element-812" data-mp="3dxievht4nwyy">
                {task.taskType === 'Tilbudsopgave' ? (task.quotePrice || 0).toLocaleString('da-DK') : (task.totalPrice || 0).toLocaleString('da-DK')}{' '}
                kr
              </span>
            </div>
          </div>}
      </div>
    </motion.article>;
}